import React, { useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

interface IReportPoint {
  date: string
  count: number
}

interface IReportMarker {
  type: string
  date: string
  case_id: number | null
}

export interface ITrademarkInfringementsReport {
  hidden?: boolean
  first_found?: string | null
  removed_at?: string | null
  points?: IReportPoint[]
  markers?: IReportMarker[]
}

interface IChartPoint {
  x: number
  y: number
  synthetic?: boolean
}

interface IMarkerLabelViewBox {
  x: number
  y: number
  width: number
  height: number
}

const LINE_COLOR = '#0E63D8'

const DOT_BORDER_COLOR = '#FFFFFF'

const AXIS_LINE_COLOR = '#ccd6eb'

const MARKER_COLORS: Record<string, { color: string; backgroundColor: string }> = {
  submitted: { color: '#BDAA00', backgroundColor: '#FFE9242E' },
  accepted: { color: '#00BD7B', backgroundColor: '#24FFB226' },
  disappeared: { color: '#557191', backgroundColor: '#55719114' },
}

const DAY_MS = 24 * 60 * 60 * 1000

const TICK_STEP_DAYS = 2

const AXIS_TICK_STYLE = {
  fill: 'rgba(0,50,109,0.3)',
  fontSize: 13,
  fontFamily: 'Jost, sans-serif',
}

const MARKER_LABEL_CHAR_WIDTH = 6.4
const MARKER_LABEL_PADDING_X = 6
const MARKER_LABEL_HEIGHT = 20
const MARKER_LABEL_GAP = 4

const toTimestamp = (value: string) => {
  const normalized = value.includes(' ') ? value.replace(' ', 'T') + 'Z' : value

  return Date.parse(normalized)
}

const formatAxisDate = (timestamp: number) => moment.utc(timestamp).format('D MMM')

const formatTooltipDate = (timestamp: number) => moment.utc(timestamp).format('dddd, D MMM')

const MarkerLabel = ({
  viewBox,
  text,
  color,
  backgroundColor,
}: {
  viewBox: IMarkerLabelViewBox
  text: string
  color: string
  backgroundColor: string
}) => {
  const label = text.toUpperCase()
  const width = label.length * MARKER_LABEL_CHAR_WIDTH + MARKER_LABEL_PADDING_X * 2
  const top = viewBox.y - MARKER_LABEL_GAP - MARKER_LABEL_HEIGHT

  return (
    <g>
      <rect
        fill={backgroundColor}
        height={MARKER_LABEL_HEIGHT}
        rx={4}
        width={width}
        x={viewBox.x - width / 2}
        y={top}
      />
      <text
        dominantBaseline='central'
        fill={color}
        style={{ fontWeight: 600, fontSize: '10px', fontFamily: 'Jost, sans-serif' }}
        textAnchor='middle'
        x={viewBox.x}
        y={top + MARKER_LABEL_HEIGHT / 2}
      >
        {label}
      </text>
    </g>
  )
}

export const TrademarkInfringementsReportGraph = ({
  data,
}: {
  data?: ITrademarkInfringementsReport
}) => {
  const { t } = useTranslation()

  const seriesData = useMemo(
    () =>
      (data?.points ?? [])
        .map((point) => [toTimestamp(point.date), point.count] as [number, number])
        .filter(([ts]) => !Number.isNaN(ts))
        .sort((a, b) => a[0] - b[0]),
    [data?.points],
  )

  const markers = useMemo(
    () =>
      (data?.markers ?? [])
        .map((marker) => ({
          type: marker.type,
          ts: toTimestamp(marker.date),
          colors: MARKER_COLORS[marker.type],
        }))
        .filter(({ ts, colors }) => !Number.isNaN(ts) && !!colors),
    [data?.markers],
  )

  const todayTs = useMemo(() => {
    const now = new Date()

    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  }, [])

  const minTs = useMemo(() => {
    const pointTs = seriesData.map(([ts]) => ts)

    return pointTs.length ? Math.min(...pointTs) : todayTs
  }, [seriesData, todayTs])

  const tickPositions = useMemo(() => {
    const positions: number[] = []

    for (let ts = todayTs; ts >= minTs; ts -= TICK_STEP_DAYS * DAY_MS) positions.unshift(ts)

    return positions
  }, [minTs, todayTs])

  const xDomain = useMemo<[number, number]>(
    () => (minTs < todayTs ? [minTs, todayTs] : [todayTs - DAY_MS, todayTs]),
    [minTs, todayTs],
  )

  const chartData = useMemo<IChartPoint[]>(() => {
    if (!seriesData.length) return []

    const points = seriesData.map(([x, y]) => ({ x, y }))
    const [lastTs] = seriesData[seriesData.length - 1]

    if (todayTs <= lastTs) return points

    const nextDayTs = lastTs + DAY_MS

    return [
      ...points,
      ...(nextDayTs < todayTs ? [{ x: nextDayTs, y: 0, synthetic: true }] : []),
      { x: todayTs, y: 0, synthetic: true },
    ]
  }, [seriesData, todayTs])

  return (
    <div className='h-[300px] w-full'>
      <ResponsiveContainer height='100%' width='100%'>
        <LineChart data={chartData} margin={{ top: 32, right: 32, bottom: 0, left: 0 }}>
          <CartesianGrid stroke='rgba(0,50,109,0.03)' vertical={false} />
          <XAxis
            tickFormatter={(value: number) =>
              value === todayTs
                ? t('pages.trademarkInfringements.report.today')
                : formatAxisDate(value)
            }
            axisLine={{ stroke: AXIS_LINE_COLOR }}
            dataKey='x'
            domain={xDomain}
            scale='time'
            tick={AXIS_TICK_STYLE}
            tickLine={false}
            ticks={tickPositions}
            type='number'
          />
          <YAxis
            tickFormatter={(value: number) =>
              t('pages.trademarkInfringements.report.countAds', { count: value })
            }
            allowDecimals={false}
            axisLine={false}
            tick={AXIS_TICK_STYLE}
            tickLine={false}
            width='auto'
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length || typeof label !== 'number') return null

              return (
                <div className='rounded-[15px] bg-white px-[12px] py-[8px] text-[14px] text-[#00326D] shadow-sm'>
                  <p className='mb-[4px]'>{formatTooltipDate(label)}</p>
                  <p className='font-bold'>
                    {t('pages.trademarkInfringements.report.countAds', {
                      count: Number(payload[0].value ?? 0),
                    })}
                  </p>
                </div>
              )
            }}
          />
          {markers.map(({ ts, type, colors }, index) => (
            <ReferenceLine
              key={`${type}-${ts}-${index}`}
              label={({ viewBox }: { viewBox: IMarkerLabelViewBox }) => (
                <MarkerLabel
                  backgroundColor={colors.backgroundColor}
                  color={colors.color}
                  text={t(`pages.trademarkInfringements.latestComplaintStatus.${type}`)}
                  viewBox={viewBox}
                />
              )}
              stroke={colors.color}
              strokeDasharray='4 4'
              x={ts}
            />
          ))}
          <Line
            dot={({ cx, cy, index, payload }) =>
              (payload as IChartPoint)?.synthetic ? (
                <g key={`dot-${index}`} />
              ) : (
                <circle
                  key={`dot-${index}`}
                  cx={cx}
                  cy={cy}
                  fill={LINE_COLOR}
                  r={4}
                  stroke={DOT_BORDER_COLOR}
                  strokeWidth={2}
                />
              )
            }
            activeDot={{ r: 5, fill: LINE_COLOR, stroke: DOT_BORDER_COLOR, strokeWidth: 2 }}
            dataKey='y'
            stroke={LINE_COLOR}
            strokeWidth={2}
            type='linear'
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
